import { normalizeCoupon } from '@/features/shop/utils/normalizeCoupon'
import { emailSchema, studentEmailSchema } from '@/features/shared/utils/validations'
import {
  type Content,
  type Section,
  type HeaderTagTreatment,
  type AdMetadata,
} from '@/features/cms/components/CMS/types'
import { logError } from '@/features/shared/utils/logger'
import { isOnServer } from '@shared/constants/util'
import { trackSponsoredContentLoaded } from '@/features/shared/analytics/promotions'
import { datadogRum } from '@datadog/browser-rum'
import { isObject } from '@/features/shared/utils/isObject'

export const singleEntryFormValidations = {
  DEFAULT: emailSchema,
  STUDENT_EMAIL_VALIDATION: studentEmailSchema,
  EMAIL_VALIDATION: emailSchema,
} as const

export function getTagTreatment(
  fieldName: string,
  header_tag_treatment: HeaderTagTreatment['header_tag_treatment'],
) {
  const tag = header_tag_treatment?.find((treatment) => treatment.field === fieldName)?.tag
  return tag === 'h1' || tag === 'h2' || tag === 'h3' ? tag : undefined
}

export const getCouponsFromCMSContent = (content: Content) => {
  // https://app.clickup.com/t/86b4xx2n6
  const coupons =
    content.content_type_id === 'product_shelf' || content.content_type_id === 'enhanced_product'
      ? (content.data.coupons ?? [])
      : []
  return coupons.map(normalizeCoupon)
}

export const loadBeaconsForAdType = ({
  aggregate_onLoadBeacons,
  load_beacon,
  featuredTransactionID = '',
  ad_placement_id,
}: Partial<
  Pick<
    AdMetadata,
    'aggregate_onLoadBeacons' | 'load_beacon' | 'featuredTransactionID' | 'ad_placement_id'
  >
>) => {
  if (aggregate_onLoadBeacons && aggregate_onLoadBeacons.length > 0) {
    aggregate_onLoadBeacons.forEach((beaconUrl) => {
      trackSponsoredContentLoaded({
        beaconUrl,
        featuredTransactionID,
        adPlacementId: ad_placement_id,
      })
    })
  } else if (load_beacon) {
    trackSponsoredContentLoaded({
      beaconUrl: load_beacon,
      featuredTransactionID,
      adPlacementId: ad_placement_id,
    })
  }
}

export const loadBeaconsFromCMSContent = (section: Section) => {
  section.forEach((data) => {
    const { content_type_id } = data

    if (content_type_id === 'product_shelf') {
      const {
        aggregate_onLoadBeacons,
        featuredTransactionID = '',
        ad_placement_id,
      } = data.metadata?.read_operation_info ?? {}
      if (aggregate_onLoadBeacons) {
        aggregate_onLoadBeacons.forEach((beaconUrl) => {
          trackSponsoredContentLoaded({
            beaconUrl,
            featuredTransactionID,
            adPlacementId: ad_placement_id,
          })
        })
      }
    }

    if (content_type_id === 'ad_banner') {
      loadBeaconsForAdType(data.metadata.read_operation_info ?? {})
    }
  })
}

type ExperimentInfo = {
  ab_experiment_key: string
  ab_variation_key: string
}

const isExperimentInfo = (value: unknown): value is ExperimentInfo =>
  isObject(value) &&
  typeof value.ab_experiment_key === 'string' &&
  typeof value.ab_variation_key === 'string'

const findExperimentInfo = (obj: unknown): void => {
  if (Array.isArray(obj)) {
    obj.forEach(findExperimentInfo)
    return
  }

  if (!isObject(obj)) return

  if (isObject(obj.metadata) && isExperimentInfo(obj.metadata.experiment_info)) {
    datadogRum.addFeatureFlagEvaluation(
      `cms_exp_${obj.metadata.experiment_info.ab_experiment_key}`,
      obj.metadata.experiment_info.ab_variation_key,
    )
  }

  Object.values(obj).forEach(findExperimentInfo)
}

export const trackCmsExperimentsInDatadog = (section: Section) => {
  if (isOnServer()) return

  try {
    findExperimentInfo(section)
  } catch (e) {
    // dont wanna accidentally bring down the app
    logError(
      new Error('Unable to sync CMS experiments with Datadog RUM', {
        cause: e,
      }),
    )
  }
}
